/**
 * /venues/:venueSlug/admin/tv — what the screen on the wall shows.
 *
 * Everything on this page is about a display nobody is standing at, which is
 * why it is built the way it is:
 *
 *   - THE PREVIEWS ARE THE WALL'S OWN COMPONENT. AdSlotPreview comes from
 *     TvAds.jsx, the file the TV renders from, in the slot's aspect ratio and
 *     with the same `contain` treatment. An admin uploading a phone photo can
 *     see the letterboxing they are going to get before it is two metres wide
 *     in front of customers.
 *   - THE TIMINGS SAY THEIR OWN BOUNDS. sidebarTiming clamps whatever is in the
 *     table, so a number typed here can be silently ignored at render time.
 *     Showing the range is cheaper than explaining afterwards why "5" became
 *     thirty seconds.
 *   - NOTHING IS SAVED UNTIL SAVE IS PRESSED, for the settings half. The ads
 *     half is different: an upload is a file transfer and a remove is
 *     destructive, so both happen immediately and say so.
 *
 * TWO ENDPOINTS, TWO ROLES, and the page does not pretend otherwise. /ads is
 * venue_admin and so is PUT /settings/tv — see that file's header for why the
 * TV's keys get their own narrow route instead of the owner-only /settings.
 *
 * Reads come from /settings/public, which is where the TV and the spectator
 * view read them too. One allowlist, one shape, no second read path.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPut, apiDel, apiUpload } from '../lib/api.js';
import { useVenue } from '../context/AuthContext.jsx';
import {
  AdSlotPreview, sidebarTiming, tvToggles, SIDEBAR_DEFAULTS, TOGGLE_DEFAULTS,
} from '../components/tv/TvAds.jsx';

/**
 * The three timings, with the bounds sidebarTiming will hold them to.
 *
 * `max` for the display time is the WORD "the interval" rather than a number,
 * because that is what the clamp actually does — it caps display at whatever
 * interval happens to be, so the standings always hold the panel for at least
 * as long as the ad that interrupted them.
 */
const TIMINGS = [
  {
    key:   'tv_sidebar_interval_seconds',
    label: 'Between interrupts',
    hint:  `Darts between one sidebar ad and the next. 30s–3h, ${SIDEBAR_DEFAULTS.intervalSeconds}s if unset.`,
    min:   30,
    max:   3 * 60 * 60,
  },
  {
    key:   'tv_sidebar_display_seconds',
    label: 'Ad stays up for',
    hint:  `How long it holds the panel. 5s–300s, and never longer than the interval. ${SIDEBAR_DEFAULTS.displaySeconds}s if unset.`,
    min:   5,
    max:   300,
  },
  {
    key:   'tv_sidebar_warmup_seconds',
    label: 'First interrupt after',
    hint:  'The wait before the first one, from the screen coming on. 0s–3h; one full interval if unset.',
    min:   0,
    max:   3 * 60 * 60,
  },
];

const TOGGLES = [
  {
    key:     'tv_live_averages',
    label:   'Live averages on the boards',
    hint:    'Shows each player’s three-dart average during a match, not just after it. Off by default: mid-match an average swings wildly on one visit.',
    fallback: TOGGLE_DEFAULTS.liveAverages,
  },
  {
    key:     'tv_follow_qr',
    label:   'Follow-along QR code',
    hint:    'Puts a code on the wall that opens tonight’s live scores on a spectator’s own phone. On by default.',
    fallback: TOGGLE_DEFAULTS.followQr,
  },
  {
    key:     'tv_total_raised_enabled',
    label:   'Total raised takeover',
    hint:    'Enables the Start/Stop control on the tournament page, which puts a charity total over the whole screen for a presentation.',
    fallback: TOGGLE_DEFAULTS.totalRaised,
  },
];

/** The two rotations, plus the stand-in. Order is the page's reading order. */
const AD_SLOTS = [
  {
    kind:  'sidebar',
    title: 'Sidebar ads',
    blurb: 'Portrait, 9:16. These interrupt the standings during play for the time set above, then give the panel back.',
  },
  {
    kind:  'fullscreen',
    title: 'Fullscreen ads',
    blurb: 'Landscape, 16:9. The whole screen before the draw and after the night ends — the windows where there are no darts to show.',
  },
];

/* ------------------------------------------------------------------------- */

/**
 * One upload button.
 *
 * The input is reset after every attempt, including a failed one: without it,
 * choosing the same file twice in a row fires no change event and the second
 * attempt looks like a dead button.
 */
function UploadButton({ label, busy, onFile }) {
  return (
    <label className="hd-btn hd-btn-secondary">
      {busy ? 'Uploading…' : label}
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        disabled={busy}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFile(file);
        }}
      />
    </label>
  );
}

/* ------------------------------------------------------------------------- */

export default function AdminTvSettings() {
  const { venueSlug } = useParams();
  const contextVenue = useVenue(venueSlug);
  const contextId = contextVenue?.venue_id ?? null;

  const [venueId, setVenueId] = useState(contextId);
  const [status, setStatus]   = useState('loading');
  const [error, setError]     = useState(null);

  // The saved values, and the form's working copy. Kept apart so Save can post
  // only what moved and Cancel has something to go back to.
  const [saved, setSaved] = useState({});
  const [form, setForm]   = useState({});

  const [ads, setAds]         = useState([]);
  const [uploading, setUpload] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [notice, setNotice]   = useState(null);

  const loadAll = useCallback(async (id) => {
    const [settings, adList] = await Promise.all([
      apiGet(`/api/venues/${id}/settings/public`),
      apiGet(`/api/venues/${id}/ads`),
    ]);

    if (settings.error) return { error: settings.error };

    const values = settings.data?.settings ?? {};

    return {
      settings: values,
      // An ads read that fails is not fatal to this page — the timings and
      // toggles are still editable, and the ad lists render empty with the
      // error shown once rather than the whole page refusing to load.
      ads:      adList.data?.ads ?? [],
      adsError: adList.error ?? null,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    (async () => {
      let id = contextId;

      // Platform staff hold no user_venue_roles row for a venue they can still
      // administer, so the context has no entry to read the id from.
      if (id === null) {
        const { data, error: err } = await apiGet(`/api/venues/by-slug/${venueSlug}`);
        if (cancelled) return;

        if (err || !data?.venue) {
          setError(err ?? 'Venue not found');
          setStatus('error');
          return;
        }
        id = data.venue.id;
      }

      if (cancelled) return;
      setVenueId(id);

      const result = await loadAll(id);
      if (cancelled) return;

      if (result.error) {
        setError(result.error);
        setStatus('error');
        return;
      }

      setSaved(result.settings);
      setForm(result.settings);
      setAds(result.ads);
      setError(result.adsError);
      setStatus('ready');
    })();

    return () => { cancelled = true; };
  }, [venueSlug, contextId, loadAll]);

  const refreshAds = async () => {
    if (venueId == null) return;
    const { data } = await apiGet(`/api/venues/${venueId}/ads`);
    setAds(data?.ads ?? []);
  };

  /* --- the settings half ------------------------------------------------- */

  const setField = (key, value) => {
    setNotice(null);
    setForm((f) => ({ ...f, [key]: value }));
  };

  const dirty = Object.keys({ ...saved, ...form }).some((k) => (saved[k] ?? null) !== (form[k] ?? null));

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);

    // Only what moved. The endpoint filters again on its own side, but sending
    // six keys to change one would make the audit log read as if an admin had
    // rewritten the whole display.
    const body = {};
    for (const key of [...TIMINGS.map((t) => t.key), ...TOGGLES.map((t) => t.key)]) {
      if ((saved[key] ?? null) !== (form[key] ?? null)) body[key] = form[key] ?? null;
    }

    if (!Object.keys(body).length) { setSaving(false); return; }

    const { data, error: err } = await apiPut(`/api/venues/${venueId}/settings/tv`, body);
    setSaving(false);

    if (err) { setError(err); return; }

    // Re-seeded from the server's answer rather than from the form: the
    // endpoint returns every key it owns, so this also picks up anything
    // another admin changed while this page was open.
    const next = { ...form, ...(data?.settings ?? {}) };
    setSaved(next);
    setForm(next);
    setNotice('Saved. Screens already on will pick this up when they reload.');
  };

  /* --- the ads half ------------------------------------------------------ */

  const upload = async (kind, file) => {
    setUpload(kind);
    setError(null);
    setNotice(null);

    const body = new FormData();
    body.set('kind', kind);
    body.set('file', file);

    const { error: err } = await apiUpload(`/api/venues/${venueId}/ads`, body);
    setUpload(null);

    if (err) { setError(err); return; }
    await refreshAds();
  };

  const remove = async (ad) => {
    // Destructive and immediate — the object leaves the bucket, not just the
    // rotation. Worth one question, which is more than the ad rotation gets
    // anywhere else because nothing else here cannot be undone by re-uploading.
    const ok = window.confirm(
      'Remove this image?\n\nIt comes off the TV and out of storage. '
      + 'Putting it back means uploading it again.'
    );
    if (!ok) return;

    setError(null);
    const { error: err } = await apiDel(`/api/venues/${venueId}/ads/${ad.id}`);
    if (err) { setError(err); return; }
    await refreshAds();
  };

  /* ----------------------------------------------------------------------- */

  if (status === 'loading') return <div className="hd-empty">Loading…</div>;

  if (status === 'error') {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">TV display</h1>
        <p className="hd-muted">{error}</p>
      </div>
    );
  }

  const byKind = (kind) => ads.filter((a) => a.kind === kind);
  const fallback = ads.find((a) => a.kind === 'fallback') ?? null;

  // What the TV will actually do with what is in the boxes right now, run
  // through the wall's own clamp rather than re-derived here.
  const effective = sidebarTiming(form);
  const toggles   = tvToggles(form);

  return (
    <div className="hd-stack">
      <div>
        <h1 className="hd-page-title">TV display</h1>
        <p className="hd-meta">{contextVenue?.name ?? venueSlug}</p>
      </div>

      {/* Errors stay at the top: the uploads that raise most of them are up
          here too. The SAVE confirmation is not here — it sits by the Save
          button, which on a page this long is a screen and a half away. */}
      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      {/* --- ads --------------------------------------------------------- */}

      {AD_SLOTS.map(({ kind, title, blurb }) => (
        <section key={kind} className="hd-card hd-stack">
          <div className="hd-row-between">
            <div>
              <h2 className="hd-section-title" style={{ margin: 0 }}>{title}</h2>
              <p className="hd-muted" style={{ margin: 0 }}>{blurb}</p>
            </div>
            <UploadButton
              label="Add image"
              busy={uploading === kind}
              onFile={(file) => upload(kind, file)}
            />
          </div>

          {byKind(kind).length === 0 ? (
            <p className="hd-muted">
              Nothing uploaded. {kind === 'sidebar'
                ? 'The panel keeps the standings up instead of interrupting them.'
                : 'The screen shows the venue name instead.'}
            </p>
          ) : (
            <div className={`hd-ad-grid hd-ad-grid-${kind}`}>
              {byKind(kind).map((ad) => (
                <div key={ad.id} className="hd-ad-tile">
                  <AdSlotPreview ad={ad} kind={kind} />
                  <button
                    type="button"
                    className="hd-btn hd-btn-quiet"
                    onClick={() => remove(ad)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      ))}

      <section className="hd-card hd-stack">
        <div className="hd-row-between">
          <div>
            <h2 className="hd-section-title" style={{ margin: 0 }}>Stand-in image</h2>
            <p className="hd-muted" style={{ margin: 0 }}>
              One per venue. Shown wherever there is no ad of the kind a slot
              wants — in place of the plain screen with the venue’s name on it.
            </p>
          </div>
          <UploadButton
            label={fallback ? 'Replace' : 'Add image'}
            busy={uploading === 'fallback'}
            onFile={(file) => upload('fallback', file)}
          />
        </div>

        {fallback ? (
          <div className="hd-ad-tile">
            <AdSlotPreview ad={fallback} kind="fullscreen" />
            <button type="button" className="hd-btn hd-btn-quiet" onClick={() => remove(fallback)}>
              Remove
            </button>
          </div>
        ) : (
          <p className="hd-muted">Not set. Empty slots show the venue name.</p>
        )}
      </section>

      {/* --- timings and toggles ----------------------------------------- */}

      <section className="hd-card hd-stack">
        <h2 className="hd-section-title" style={{ margin: 0 }}>Sidebar timing</h2>
        <p className="hd-muted" style={{ margin: 0 }}>
          How often the sidebar ads interrupt the standings during play. Leave a
          box empty to use the default.
        </p>

        {TIMINGS.map(({ key, label, hint, min, max }) => (
          <div key={key} className="hd-field">
            <label className="hd-label" htmlFor={key}>{label}</label>
            <input
              id={key}
              className="hd-input"
              type="number"
              min={min}
              max={max}
              step="1"
              value={form[key] ?? ''}
              placeholder="Default"
              onChange={(e) => setField(key, e.target.value === '' ? null : e.target.value)}
            />
            <p className="hd-hint">{hint}</p>
          </div>
        ))}

        {/* The numbers after the clamp, which is the thing that decides what
            happens on the wall. A venue that types 5 into the display box sees
            it read back as 5s only if 5s is what it will get. */}
        <p className="hd-hint">
          As set: an ad every {Math.round(effective.everyMs / 1000)}s, on screen
          for {Math.round(effective.forMs / 1000)}s, first one after{' '}
          {Math.round(effective.warmupMs / 1000)}s.
        </p>
      </section>

      <section className="hd-card hd-stack">
        <h2 className="hd-section-title" style={{ margin: 0 }}>What the screen shows</h2>

        {TOGGLES.map(({ key, label, hint, fallback: dflt }) => {
          const value = form[key] ?? null;
          const on = value === '1' || (value === null && dflt);

          return (
            <div key={key} className="hd-field">
              <label className="hd-row" style={{ gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => setField(key, e.target.checked ? '1' : '0')}
                />
                <span>{label}</span>
              </label>
              <p className="hd-hint">{hint}</p>
            </div>
          );
        })}

        {toggles.followQr && (
          <p className="hd-hint">
            The code opens tonight’s live scores. There is nothing to configure
            — it finds the night the same way the TV does.
          </p>
        )}
      </section>

      <div className="hd-row">
        <button
          type="button"
          className="hd-btn hd-btn-primary"
          disabled={!dirty || saving}
          onClick={save}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>

        {dirty && !saving && (
          <button
            type="button"
            className="hd-btn hd-btn-quiet"
            onClick={() => { setForm(saved); setNotice(null); setError(null); }}
          >
            Cancel
          </button>
        )}

        {notice && (
          <span className="hd-badge hd-badge-success" role="status">{notice}</span>
        )}

        {/* Ads are already live; only this half is pending. Saying which is
            which stops "Save" reading as if the uploads had not taken yet. */}
        {!notice && (
          <span className="hd-hint">
            {dirty ? 'Unsaved changes to timing and toggles.' : 'Uploads take effect immediately.'}
          </span>
        )}
      </div>
    </div>
  );
}
